(() => {
  const SELECTOR = '[data-cp-card-gallery]';
  const SWIPE_DISTANCE = 36;
  const SWIPE_SLOPE = 1.1;

  function initGallery(root) {
    if (!root || root.dataset.cpGalleryReady === 'true') return;

    const slides = Array.from(root.querySelectorAll('[data-cp-card-gallery-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-cp-card-gallery-dot]'));
    const prev = root.querySelector('[data-cp-card-gallery-prev]');
    const next = root.querySelector('[data-cp-card-gallery-next]');
    const productLink = root.querySelector('[data-cp-card-gallery-product-link]');
    if (!slides.length) return;

    root.dataset.cpGalleryReady = 'true';
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    if (index < 0) index = 0;
    let startX = null;
    let startY = null;
    let pointerId = null;
    let suppressClickUntil = 0;

    const show = (rawIndex, focusDot = false) => {
      index = (rawIndex + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (focusDot && dots[index]) dots[index].focus({ preventScroll: true });
    };

    const consume = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    prev?.addEventListener('click', (event) => {
      consume(event);
      show(index - 1);
    });
    next?.addEventListener('click', (event) => {
      consume(event);
      show(index + 1);
    });
    dots.forEach((dot) => dot.addEventListener('click', (event) => {
      consume(event);
      show(Number(dot.dataset.cpCardGalleryDot || 0));
    }));

    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { consume(event); show(index - 1, true); }
      if (event.key === 'ArrowRight') { consume(event); show(index + 1, true); }
    });

    const rememberStart = (x, y) => {
      startX = x;
      startY = y;
    };
    const finishSwipe = (x, y, event) => {
      if (startX === null || startY === null) return;
      const dx = x - startX;
      const dy = y - startY;
      startX = startY = null;
      if (Math.abs(dx) >= SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy) * SWIPE_SLOPE) {
        if (event?.cancelable) event.preventDefault();
        event?.stopPropagation?.();
        suppressClickUntil = Date.now() + 650;
        show(index + (dx < 0 ? 1 : -1));
      }
    };

    if (slides.length > 1) {
      /* Pointer events cover current Safari/Chrome/Edge. */
      root.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || event.button !== 0 || event.target.closest('button')) return;
        pointerId = event.pointerId;
        rememberStart(event.clientX, event.clientY);
      }, { passive: true });
      root.addEventListener('pointerup', (event) => {
        if (pointerId !== event.pointerId) return;
        pointerId = null;
        finishSwipe(event.clientX, event.clientY, event);
      });
      root.addEventListener('pointercancel', () => {
        pointerId = null;
        startX = startY = null;
      });

      /* Touch fallback for embedded/older WebKit contexts. Avoid double handling
         when PointerEvent is available. */
      if (!window.PointerEvent) {
        root.addEventListener('touchstart', (event) => {
          if (event.touches.length !== 1 || event.target.closest('button')) return;
          rememberStart(event.touches[0].clientX, event.touches[0].clientY);
        }, { passive: true });
        root.addEventListener('touchend', (event) => {
          if (!event.changedTouches.length) return;
          finishSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY, event);
        }, { passive: false });
      }
    }

    /* Cancel the synthetic click that Safari emits after a horizontal swipe. */
    root.addEventListener('click', (event) => {
      if (Date.now() <= suppressClickUntil && event.target.closest('[data-cp-card-gallery-product-link]')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    productLink?.addEventListener('dragstart', (event) => event.preventDefault());
    show(index);
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTOR)) initGallery(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initGallery);
  }

  const boot = () => initAll(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:reorder', () => initAll(document));

  const observer = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) initAll(node);
    }));
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
